import React from 'react';
import { Flex, TextV2, Box } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { SkeletonText } from '@shared/components/Skeleton';
import { ReactComponent as SearchChrevon } from '@assets/icons/search-forward-chevron.svg';
import { StyledSearchButton, StyledLine, styles } from './CatalogSearchHeader.styles';
import { useCatalogSearchHeader } from './useCatalogSearchHeader';
import { useShopStorefrontContext } from '../ShopStorefrontProvider';
import { BrowserButtonSection } from '../BrowserButtonSection';
import { useCatalogContext } from '../../state/Catalog';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useFeatureValue } from '@shared/core/featureFlags';
import { MultipleItemsFoundHeaderV2, UniversalSearchHeader } from './UniversalSearchHeader';
import { Link } from '@react-router';
import { useCatalogRoutePaths } from '../../Catalog.routes';
import { ChevronBack } from '@withjoy/joykit/icons';

interface CatalogSearchHeaderProps {
  isLoading: boolean;
  searchTerm: string;
  totalFoundBySearch: number;
}

const TotalItemsMessage = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box>
      <TextV2
        typographyVariant="body1"
        fontSize={[400]}
      >
        {children}
      </TextV2>
    </Box>
  );
};

TotalItemsMessage.Skeleton = () => {
  return (
    <Flex alignItems="center">
      <SkeletonText
        width="170px"
        skeletonHeight="14px"
        rows={1}
      />
    </Flex>
  );
};

const LegacyHeader = (props: CatalogSearchHeaderProps) => {
  const { handleClickModifySearch } = useCatalogSearchHeader();
  const { eventId, isAdminView } = useCatalogContext();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { t2 } = useTranslation('catalogRegistry');
  const t2Search = t2('searchResult');
  const { currentStorefront } = useShopStorefrontContext();
  const modifySearchExplore = t2Search.modifySearchExplore[currentStorefront];
  const { isLoading, searchTerm, totalFoundBySearch } = props;
  const { t } = useTranslation('catalogRegistry');
  const tSearchHeader = t('searchHeader');

  return (
    <>
      {isLoading && (
        <TextV2
          typographyVariant="hed4"
          fontSize={17}
        >
          {tSearchHeader.titleSearching()}
        </TextV2>
      )}
      {isLoading && (
        <TextV2
          typographyVariant={'body1'}
          marginY={[5]}
          fontSize={[1200, 1400]}
          fontFamily="Austin News Deck"
        >
          {`\u201C${searchTerm}\u201D`}
        </TextV2>
      )}
      {totalFoundBySearch > 0 && (
        <MultipleItemsFoundHeaderV2
          searchTerm={searchTerm}
          totalFoundBySearch={totalFoundBySearch}
        />
      )}
      {!isLoading && totalFoundBySearch <= 0 && (
        <Flex
          flexDirection={'column'}
          alignItems={'center'}
          marginTop={7}
        >
          <TextV2
            typographyVariant="hed4"
            fontSize={17}
            marginBottom={5}
          >
            {tSearchHeader.subTitleNoItemsFound()}
          </TextV2>
          <TextV2
            typographyVariant={'body1'}
            marginBottom={[5]}
            fontSize={[1200, 1400]}
            fontFamily="Austin News Deck"
          >
            {`\u201C${searchTerm}\u201D`}
          </TextV2>
          <StyledSearchButton
            __css={styles.searchButton}
            as="button"
            onClick={handleClickModifySearch}
          >
            <SearchChrevon css={styles.searchChrevonStyles} />
            {t2Search.modifySearchButtonText}
          </StyledSearchButton>
          {!isMobileOrTablet && eventId && isAdminView && <BrowserButtonSection eventId={eventId} />}
          <Flex
            alignItems={'center'}
            justifyContent={'space-between'}
            margin={[7]}
          >
            <StyledLine
              __css={styles.line}
              marginRight={[7]}
            />
            {t2Search.modifySearchText}
            <StyledLine
              __css={styles.line}
              marginLeft={[7]}
            />
          </Flex>
          <Box
            marginBottom={[6]}
            fontSize={[900]}
            fontFamily={'Austin News Deck Web'}
          >
            {modifySearchExplore}
          </Box>
        </Flex>
      )}
    </>
  );
};

const Container: React.FC = ({ children }) => {
  return (
    <Flex
      flexDirection={'column'}
      alignItems={'center'}
      width="100%"
      marginTop={6}
      marginBottom={5}
      rowGap={3}
      paddingX={6}
    >
      {children}
    </Flex>
  );
};

export const CatalogSearchHeader = (props: CatalogSearchHeaderProps) => {
  const { isLoading, totalFoundBySearch, searchTerm } = props;
  const { value: isUniversalSearchEnabled, loading: loadingFF } = useFeatureValue('universalCatalogSearchEnabled');

  const { shopRootPath } = useCatalogRoutePaths();
  const isMobileOrTablet = useIsMobileOrTablet();

  const { t } = useTranslation('catalogRegistry');
  const tSearchHeader = t('searchHeader');

  if (isLoading || loadingFF) {
    return (
      <Container>
        <TextV2 typographyVariant="hed2">
          {isLoading ? tSearchHeader.titleSearching() : totalFoundBySearch <= 0 ? tSearchHeader.subTitleNoItemsFound() : tSearchHeader.titleShowResult()}
        </TextV2>
        <TextV2 typographyVariant={['HedMobile32', null, null, 'Hed56']}>{`\u201C${searchTerm}\u201D`}</TextV2>
      </Container>
    );
  }

  return (
    <Container>
      <Flex
        justifyContent="center"
        alignSelf={['start', null, null, 'normal']}
        columnGap={4}
        typographyVariant={'hed2'}
      >
        <Link to={shopRootPath}>
          {isMobileOrTablet && (
            <ChevronBack
              size="sm"
              marginRight={4}
            />
          )}
          {tSearchHeader.breadcrumbs.root()}
        </Link>
        {!isMobileOrTablet && (
          <>
            <span>/</span>
            <Box
              as="span"
              color="mono10"
            >
              {tSearchHeader.breadcrumbs.current()}
            </Box>
          </>
        )}
      </Flex>
      {isUniversalSearchEnabled ? <UniversalSearchHeader {...props} /> : <LegacyHeader {...props} />}
    </Container>
  );
};
